import Image from 'next/image'
import {FC, useCallback, useEffect, useRef, useState} from 'react'

import {Spinner} from '@/components/Spinner'
import {useVideoContent} from '@/content/video'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

type VideoIframeProps = {
  src: string
  thumbnail: {src: string; alt: string}
  /** Rendered width of the player, so the thumbnail isn't fetched at full viewport width. */
  sizes: string
  /** Set only when the player is in the hero; elsewhere the thumbnail stays lazy. */
  isPriority?: boolean
  hasBoxShadow?: boolean
  onClick?: () => void
}

// iPadOS reports a Mac user agent, so it's told apart by touch support
const isIos = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)

enum PlaybackState {
  // iframe not mounted yet
  idle = 'idle',
  // iframe mounted, loading in the background, user hasn't clicked
  preloading = 'preloading',
  // iframe loaded, waiting for a click
  ready = 'ready',
  // user clicked before the iframe finished loading - play once it does
  waiting = 'waiting',
  // thumbnail hidden, video playing
  playing = 'playing',
}

export const VideoIframe: FC<VideoIframeProps> = ({
  src,
  thumbnail,
  sizes,
  isPriority,
  hasBoxShadow,
  onClick,
}) => {
  const {playButton} = useVideoContent()
  const [state, setState] = useState<PlaybackState>(PlaybackState.idle)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const isIframeLoadedRef = useRef(false)

  // enablejsapi=1 allows postMessage control; playsinline=1 prevents iOS from opening the native player
  const iframeSrc = `${src}&enablejsapi=1&playsinline=1`

  const isIdle = state === PlaybackState.idle
  const isPreloading = state === PlaybackState.preloading
  const isReady = state === PlaybackState.ready
  const isWaitingToPlay = state === PlaybackState.waiting
  const isPlaying = state === PlaybackState.playing

  const isIframeMounted = !isIdle
  const showThumbnail = !isPlaying

  // The embed costs ~1.2 MB and third-party cookies, so it mounts only once the visitor shows
  // intent to play (mouse hover/focus); click is the fallback.
  const preloadIframe = useCallback(() => {
    setState((current) => (current === PlaybackState.idle ? PlaybackState.preloading : current))
  }, [])

  // iOS plays with sound only if the tap reaches an already loaded player, so there the embed
  // mounts as the player nears the viewport instead
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!isIdle || !wrapper || !isIos()) {
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          preloadIframe()
        }
      },
      {rootMargin: '400px'},
    )
    observer.observe(wrapper)

    return () => observer.disconnect()
  }, [isIdle, preloadIframe])

  // Playing unmounts the focused thumbnail, which drops focus to <body> and makes the next Tab skip
  // the player. Hand focus to the player so keyboard users can reach its controls.
  useEffect(() => {
    if (isPlaying && document.activeElement === document.body) {
      iframeRef.current?.focus()
    }
  }, [isPlaying])

  const sendPlayCommand = () => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({event: 'command', func: 'playVideo', args: []}),
      '*',
    )
  }

  // The play command is sent synchronously within the click (what iOS accepts as a gesture); if the
  // iframe isn't loaded yet it's retried on load, outside the gesture, so Safari may need a second tap.
  const handleClick = () => {
    if (isWaitingToPlay || isPlaying) {
      return
    }

    sendPlayCommand()

    if (isReady) {
      setState(PlaybackState.playing)
      onClick?.()
    } else {
      // idle or preloading: the iframe isn't loaded yet, so the command above was a no-op.
      // Retry once `onLoad` below fires.
      setState(PlaybackState.waiting)
      // WebKit carries a tap's gesture into timers started within it for 1 s, so poll from a timer
      // chain: if the player loads within that window, Safari still plays on this tap
      const startedAt = performance.now()
      const playOnceLoaded = () => {
        if (isIframeLoadedRef.current) {
          sendPlayCommand()
        } else if (performance.now() - startedAt < 1000) {
          window.setTimeout(playOnceLoaded, 50)
        }
      }
      window.setTimeout(playOnceLoaded, 50)
    }
  }

  const handleIframeLoad = () => {
    isIframeLoadedRef.current = true
    if (isWaitingToPlay) {
      sendPlayCommand()
      setState(PlaybackState.playing)
      onClick?.()
    } else if (isPreloading) {
      setState(PlaybackState.ready)
    }
  }

  return (
    <>
      <div className="wrapper" ref={wrapperRef}>
        {isIframeMounted && (
          <iframe
            ref={iframeRef}
            width="100%"
            height="100%"
            src={iframeSrc}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            // Preloaded behind the thumbnail, so keep the hidden player controls out of the tab
            // order (and away from screen readers) until it plays
            tabIndex={isPlaying ? undefined : -1}
            aria-hidden={!isPlaying}
            onLoad={handleIframeLoad}
          />
        )}
        {showThumbnail && (
          <button
            type="button"
            onClick={handleClick}
            // Not on touch: iOS Safari treats a tap that changes the DOM as a hover and drops its click
            onPointerEnter={(event) => {
              if (event.pointerType === 'mouse') {
                preloadIframe()
              }
            }}
            onFocus={preloadIframe}
            className="thumbnail"
            aria-busy={isWaitingToPlay}
          >
            <Image
              src={thumbnail.src}
              alt={thumbnail.alt}
              fill
              sizes={sizes}
              priority={isPriority}
              // A hero thumbnail is the LCP element on wide screens; `priority` alone preloads it at
              // the default, low image priority
              fetchPriority={isPriority ? 'high' : undefined}
              style={{objectFit: 'cover'}}
            />
            <span className="play-button">
              {isWaitingToPlay ? (
                <Spinner />
              ) : (
                <Image
                  src={playButton.src}
                  alt={playButton.alt}
                  fill
                  priority={isPriority}
                  sizes={`${MOBILE_SCREENSIZE} 100px, 136px`}
                  style={{objectFit: 'contain'}}
                />
              )}
            </span>
          </button>
        )}
      </div>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          aspect-ratio: 16 / 9;
          border-radius: 16px;
          overflow: hidden;
          ${hasBoxShadow ? `box-shadow: 0 24px 37px 0 rgba(207, 101, 88, 0.25);` : ''};
        }
        .wrapper:has(.thumbnail:focus-visible) {
          outline: var(--focus-outline);
          outline-offset: 2px;
        }
        .thumbnail:focus-visible {
          outline: none;
        }
        iframe {
          border: 0;
        }
        .thumbnail {
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
        .play-button {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          width: 136px;
          height: 100px;
        }
        @media ${MOBILE_SCREENSIZE} {
          .play-button {
            width: 100px;
          }
        }
      `}</style>
    </>
  )
}
